// frontend/src/services/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://imvelaphi-production.up.railway.app';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
  }

  // Get headers with authentication
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Generic fetch method with error handling and retries
  async fetchWithAuth(endpoint, options = {}, retries = 3) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(`🌐 API Request (attempt ${attempt}): ${options.method || 'GET'} ${endpoint}`);
        
        const response = await fetch(url, config);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ 
            message: `HTTP ${response.status}: ${response.statusText}` 
          }));
          
          // Handle authentication errors specifically
          if (response.status === 401) {
            throw new Error('Authentication required. Please sign in again.');
          }
          
          if (response.status === 403) {
            throw new Error('Access denied. You do not have permission for this action.');
          }
          
          throw new Error(errorData.message || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log(`✅ API Response: ${endpoint}`, data);
        return data;
      } catch (error) {
        console.error(`❌ API Error (attempt ${attempt}): ${endpoint}`, error.message);
        
        // Don't retry authentication errors
        if (error.message.includes('Authentication required') || error.message.includes('Access denied')) {
          throw error;
        }
        
        // If it's the last attempt, throw the error
        if (attempt === retries) {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  // Test connection
  async testConnection() {
    try {
      const response = await this.fetchWithAuth('/api/test');
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.fetchWithAuth('/health');
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get all family members for the authenticated user
  async getAllMembers(familyTreeId = 'default') {
    try {
      const queryParam = familyTreeId !== 'default' ? `?familyTreeId=${familyTreeId}` : '';
      const response = await this.fetchWithAuth(`/api/members${queryParam}`);
      return {
        success: true,
        members: response.members || [],
        count: response.count || 0,
        familyTreeId: response.familyTreeId
      };
    } catch (error) {
      console.error('Failed to fetch family members:', error);
      throw new Error(`Failed to load family members: ${error.message}`);
    }
  }

  // Get shared family members (from other users)
  async getSharedMembers() {
    try {
      const response = await this.fetchWithAuth('/api/members/shared');
      return {
        success: true,
        members: response.members || [],
        count: response.count || 0
      };
    } catch (error) {
      console.error('Failed to fetch shared family members:', error);
      throw new Error(`Failed to load shared family members: ${error.message}`);
    }
  }

  // Get single family member by ID
  async getMember(memberId) {
    try {
      const response = await this.fetchWithAuth(`/api/members/${memberId}`);
      return {
        success: true,
        member: response.member
      };
    } catch (error) {
      console.error('Failed to fetch family member:', error);
      throw new Error(`Failed to load family member: ${error.message}`);
    }
  }

  // Add new family member
  async addMember(memberData) {
    try {
      // Validate required fields
      if (!memberData.fullName || !memberData.relationship) {
        throw new Error('Full name and relationship are required');
      }

      // Clean and prepare data
      const cleanMemberData = {
        fullName: memberData.fullName.trim(),
        isibongo: memberData.isibongo ? memberData.isibongo.trim() : '',
        praiseNames: memberData.praiseNames ? memberData.praiseNames.trim() : '',
        relationship: memberData.relationship,
        birthDate: memberData.birthDate || null,
        deathDate: memberData.deathDate || null,
        lifeEvents: memberData.lifeEvents ? memberData.lifeEvents.trim() : '',
        photo: memberData.photo ? memberData.photo.trim() : null,
        familyTreeId: memberData.familyTreeId || 'default',
        isPublic: memberData.isPublic || false
      };

      // Validate data before sending
      const validation = this.validateMemberData(cleanMemberData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await this.fetchWithAuth('/api/members', {
        method: 'POST',
        body: JSON.stringify(cleanMemberData),
      });

      return {
        success: true,
        member: response.member,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to add family member:', error);
      throw new Error(`Failed to add family member: ${error.message}`);
    }
  }

  // Update family member
  async updateMember(memberId, updateData) {
    try {
      // Validate required fields
      if (!updateData.fullName || !updateData.relationship) {
        throw new Error('Full name and relationship are required');
      }

      // Clean and prepare data
      const cleanUpdateData = {
        fullName: updateData.fullName.trim(),
        isibongo: updateData.isibongo ? updateData.isibongo.trim() : '',
        praiseNames: updateData.praiseNames ? updateData.praiseNames.trim() : '',
        relationship: updateData.relationship,
        birthDate: updateData.birthDate || null,
        deathDate: updateData.deathDate || null,
        lifeEvents: updateData.lifeEvents ? updateData.lifeEvents.trim() : '',
        photo: updateData.photo ? updateData.photo.trim() : null,
        isPublic: updateData.isPublic
      };

      // Validate data before sending
      const validation = this.validateMemberData(cleanUpdateData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await this.fetchWithAuth(`/api/members/${memberId}`, {
        method: 'PUT',
        body: JSON.stringify(cleanUpdateData),
      });

      return {
        success: true,
        member: response.member,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to update family member:', error);
      throw new Error(`Failed to update family member: ${error.message}`);
    }
  }

  // Delete family member
  async deleteMember(memberId) {
    try {
      const response = await this.fetchWithAuth(`/api/members/${memberId}`, {
        method: 'DELETE',
      });

      return {
        success: true,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to delete family member:', error);
      throw new Error(`Failed to delete family member: ${error.message}`);
    }
  }

  // Share family member with another user
  async shareFamilyMember(memberId, email, permission = 'view') {
    try {
      if (!email) {
        throw new Error('Email address is required');
      }

      const response = await this.fetchWithAuth(`/api/members/${memberId}/share`, {
        method: 'POST',
        body: JSON.stringify({ email, permission }),
      });

      return {
        success: true,
        message: response.message,
        sharedWith: response.sharedWith
      };
    } catch (error) {
      console.error('Failed to share family member:', error);
      throw new Error(`Failed to share family member: ${error.message}`);
    }
  }

  // Remove sharing for a family member
  async unshareFamilyMember(memberId, userId) {
    try {
      const response = await this.fetchWithAuth(`/api/members/${memberId}/share/${userId}`, {
        method: 'DELETE',
      });

      return {
        success: true,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to remove sharing:', error);
      throw new Error(`Failed to remove sharing: ${error.message}`);
    }
  }

  // Get family tree statistics
  async getFamilyTreeStats(familyTreeId = 'default') {
    try {
      const queryParam = familyTreeId !== 'default' ? `?familyTreeId=${familyTreeId}` : '';
      const response = await this.fetchWithAuth(`/api/members/stats/overview${queryParam}`);
      return {
        success: true,
        stats: response.stats,
        familyTreeId: response.familyTreeId,
        lastUpdated: response.lastUpdated
      };
    } catch (error) {
      console.error('Failed to fetch family tree statistics:', error);
      throw new Error(`Failed to load statistics: ${error.message}`);
    }
  }

  // Get user profile
  async getUserProfile() {
    try {
      const response = await this.fetchWithAuth('/api/auth/profile');
      return {
        success: true,
        user: response.user
      };
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      throw new Error(`Failed to load user profile: ${error.message}`);
    }
  }

  // Update user profile
  async updateUserProfile(profileData) {
    try {
      const response = await this.fetchWithAuth('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      return {
        success: true,
        user: response.user,
        message: response.message
      };
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw new Error(`Failed to update user profile: ${error.message}`);
    }
  }

  // Search members by name (client-side filtering for now)
  async searchMembers(searchTerm, familyTreeId = 'default') {
    try {
      const response = await this.getAllMembers(familyTreeId);
      if (response.success) {
        const searchLower = searchTerm.toLowerCase();
        const filteredMembers = response.members.filter(member =>
          member.fullName.toLowerCase().includes(searchLower) ||
          (member.isibongo && member.isibongo.toLowerCase().includes(searchLower)) ||
          (member.praiseNames && member.praiseNames.toLowerCase().includes(searchLower))
        );
        return {
          success: true,
          members: filteredMembers,
          count: filteredMembers.length
        };
      }
      return response;
    } catch (error) {
      console.error('Failed to search family members:', error);
      throw new Error(`Failed to search family members: ${error.message}`);
    }
  }

  // Get members by relationship
  async getMembersByRelationship(relationship, familyTreeId = 'default') {
    try {
      const response = await this.getAllMembers(familyTreeId);
      if (response.success) {
        const filteredMembers = response.members.filter(member =>
          member.relationship === relationship
        );
        return {
          success: true,
          members: filteredMembers,
          count: filteredMembers.length
        };
      }
      return response;
    } catch (error) {
      console.error('Failed to fetch members by relationship:', error);
      throw new Error(`Failed to load ${relationship} members: ${error.message}`);
    }
  }

  // Get members by clan
  async getMembersByClan(isibongo, familyTreeId = 'default') {
    try {
      const response = await this.getAllMembers(familyTreeId);
      if (response.success) {
        const filteredMembers = response.members.filter(member =>
          member.isibongo && member.isibongo.toLowerCase() === isibongo.toLowerCase()
        );
        return {
          success: true,
          members: filteredMembers,
          count: filteredMembers.length
        };
      }
      return response;
    } catch (error) {
      console.error('Failed to fetch members by clan:', error);
      throw new Error(`Failed to load ${isibongo} clan members: ${error.message}`);
    }
  }

  // Validate member data before sending
  validateMemberData(memberData) {
    const errors = [];

    if (!memberData.fullName || memberData.fullName.trim().length === 0) {
      errors.push('Full name is required');
    }

    if (!memberData.relationship) {
      errors.push('Relationship is required');
    }

    if (memberData.fullName && memberData.fullName.trim().length > 100) {
      errors.push('Full name cannot exceed 100 characters');
    }

    if (memberData.isibongo && memberData.isibongo.trim().length > 50) {
      errors.push('Clan name cannot exceed 50 characters');
    }

    if (memberData.praiseNames && memberData.praiseNames.trim().length > 200) {
      errors.push('Praise names cannot exceed 200 characters');
    }

    if (memberData.lifeEvents && memberData.lifeEvents.trim().length > 1000) {
      errors.push('Life events cannot exceed 1000 characters');
    }

    if (memberData.photo && memberData.photo.trim()) {
      try {
        new URL(memberData.photo.trim());
      } catch {
        errors.push('Photo must be a valid URL');
      }
    }

    if (memberData.birthDate && new Date(memberData.birthDate) > new Date()) {
      errors.push('Birth date cannot be in the future');
    }

    if (memberData.birthDate && memberData.deathDate) {
      if (new Date(memberData.deathDate) < new Date(memberData.birthDate)) {
        errors.push('Death date cannot be before birth date');
      }
    }

    // Validate relationship
    const validRelationships = [
      'uBaba', 'uMama', 'Umkhulu', 'Ugogo', 
      'uMzala', 'uMalume', 'uBhuti', 'uSisi', 
      'iNdodana', 'iNdodakazi', 'uMakoti', 'uMkhwenyana', 'Other'
    ];

    if (memberData.relationship && !validRelationships.includes(memberData.relationship)) {
      errors.push('Invalid relationship type');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Handle offline scenarios
  async handleOfflineRequest(key, data) {
    const offlineData = localStorage.getItem(`imvelaphi_offline_${key}`);
    if (data) {
      localStorage.setItem(`imvelaphi_offline_${key}`, JSON.stringify(data));
    }
    return offlineData ? JSON.parse(offlineData) : null;
  }

  // Clear offline data when back online
  clearOfflineData(key) {
    localStorage.removeItem(`imvelaphi_offline_${key}`);
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;